import { Link, usePage } from "@inertiajs/react";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  User,
  LogOut,
  Users,
  Search,
  LayoutGrid,
  ClipboardList,
  FileText,
  BarChart3,
  ChevronDown,
  Briefcase,
  CreditCard,
  PiggyBank,
  Building2,
  ShoppingBag,
  Factory,
  ShieldCheck,
  PanelLeft,
  DollarSign,
  Database,
} from "lucide-react";
import { cn } from "@/lib/utils";
import ModuleAvatar from "@/components/ModuleAvatar";
import type { PageProps } from "@/layouts/desk/types";

interface AppSidebarProps {
  onOpenAwesome: () => void;
}

export default function AppSidebar({ onOpenAwesome }: AppSidebarProps) {
  const { auth, modules = [], current_module, module_sidebar = [] } = usePage<PageProps>().props;
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/i.test(navigator.platform);
  const currentModule = modules.find((mod) => mod.key === current_module) || modules[0] || null;
  const sidebarSections = module_sidebar;
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const { toggleSidebar, state } = useSidebar();
  const showTooltips = state === "collapsed";

  const iconForResource = (icon?: string, size = 4) => {
    const className = size === 6 ? "h-6 w-6" : "h-4 w-4";
    switch (icon) {
      case "layout-grid":
        return <LayoutGrid className={className} />;
      case "list":
        return <ClipboardList className={className} />;
      case "file":
        return <FileText className={className} />;
      case "chart":
        return <BarChart3 className={className} />;
      case "users":
        return <Users className={className} />;
      case "briefcase":
        return <Briefcase className={className} />;
      case "credit-card":
        return <CreditCard className={className} />;
      case "piggy-bank":
        return <PiggyBank className={className} />;
      case "building":
        return <Building2 className={className} />;
      case "shopping-bag":
        return <ShoppingBag className={className} />;
      case "factory":
        return <Factory className={className} />;
      case "shield-check":
        return <ShieldCheck className={className} />;
      case "currency-dollar":
      case "dollar-sign":
        return <DollarSign className={className} />;
      case "database":
        return <Database className={className} />;
      default:
        return null;
    }
  };

  const getInitials = (email: string) => email.substring(0, 2).toUpperCase();

  return (
    <Sidebar variant="sidebar" side="right" collapsible="icon">
      <SidebarHeader>
        {currentModule && (
          <DropdownMenu>
            <DropdownMenuTrigger className="w-full">
              <SidebarMenuButton className="w-full justify-between gap-3 rounded-lg border border-sidebar-border bg-sidebar py-8 group-data-[collapsible=icon]:justify-center">
                <div className="flex items-center gap-3">
                  <ModuleAvatar icon={currentModule.icon} color={currentModule.color} size="lg" />
                  <div className="grid text-start text-sm leading-tight group-data-[collapsible=icon]:hidden">
                    <span className="font-semibold text-sidebar-foreground">
                      {currentModule.label}
                    </span>
                    <span className="text-xs text-muted-foreground">رابح</span>
                  </div>
                </div>
                <ChevronDown className="h-4 w-4 text-muted-foreground group-data-[collapsible=icon]:hidden" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent sideOffset={8} align="start" className="w-56">
              {modules.map((mod) => (
                <Link key={mod.key} href={mod.path}>
                  <DropdownMenuItem>
                    <ModuleAvatar
                      icon={mod.icon}
                      color={mod.color}
                      size="sm"
                      className="shrink-0"
                    />
                    <span>{mod.label}</span>
                  </DropdownMenuItem>
                </Link>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip={showTooltips ? "بحث" : undefined}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2"
                    onClick={onOpenAwesome}
                  >
                    <Search />
                    <span className="group-data-[collapsible=icon]:hidden">بحث</span>
                    <span className="ms-auto rounded-md border border-border/60 bg-muted px-2 py-0.5 text-xs text-muted-foreground group-data-[collapsible=icon]:hidden">
                      {isMac ? "K ⌘" : "Ctrl+K"}
                    </span>
                  </button>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <div className="px-2">
                <div className="h-px bg-border/70" />
              </div>
              {sidebarSections.map((sectionGroup, sectionIndex) => {
                const sectionKey = sectionGroup.section?.key || `section-${sectionIndex}`;
                const isOpen = openSections[sectionKey] !== false;
                const hasSection = Boolean(sectionGroup.section);
                return (
                  <div key={sectionKey} className="space-y-1">
                    {hasSection && (
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          asChild
                          tooltip={showTooltips ? sectionGroup.section?.label : undefined}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              if (state === "collapsed") {
                                toggleSidebar();
                                setOpenSections((prev) => ({ ...prev, [sectionKey]: true }));
                                return;
                              }
                              setOpenSections((prev) => ({ ...prev, [sectionKey]: !isOpen }));
                            }}
                            className="justify-between"
                          >
                            <span className="flex items-center gap-2">
                              <span className="flex h-4 w-4 items-center justify-center">
                                {iconForResource(sectionGroup.section?.icon)}
                              </span>
                              <span className="group-data-[collapsible=icon]:hidden">
                                {sectionGroup.section?.label}
                              </span>
                            </span>
                            <ChevronDown
                              className={cn(
                                isOpen ? "h-4 w-4" : "h-4 w-4 rotate-90",
                                "group-data-[collapsible=icon]:hidden"
                              )}
                            />
                          </button>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )}
                    {(isOpen || !hasSection) &&
                      sectionGroup.resources.map((resource) => (
                        <SidebarMenuItem key={resource.key}>
                          <SidebarMenuButton
                            asChild
                            tooltip={showTooltips ? resource.label : undefined}
                          >
                            <Link href={resource.path}>
                              <span className="flex h-4 w-4 items-center justify-center">
                                {iconForResource(resource.icon)}
                              </span>
                              <span className="group-data-[collapsible=icon]:hidden">
                                {resource.label}
                              </span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                  </div>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <div className="px-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip={showTooltips ? "تصغير" : undefined}>
              <button
                type="button"
                className="flex w-full items-center gap-2"
                onClick={toggleSidebar}
              >
                <PanelLeft />
                <span className="group-data-[collapsible=icon]:hidden">تصغير</span>
              </button>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </div>

      <SidebarFooter>
        <DropdownMenu>
          <DropdownMenuTrigger className="w-full">
            <SidebarMenuButton className="w-full group-data-[collapsible=icon]:size-10 group-data-[collapsible=icon]:justify-center">
              <Avatar className="h-6 w-6">
                <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                  {auth?.user ? getInitials(auth.user.email) : "??"}
                </AvatarFallback>
              </Avatar>
              <span className="truncate group-data-[collapsible=icon]:hidden">
                {auth?.user?.email}
              </span>
            </SidebarMenuButton>
          </DropdownMenuTrigger>

          <DropdownMenuContent sideOffset={8} align="end" className="w-56">
            <Link href="/desk/admin">
              <DropdownMenuItem>
                <User />
                <span>الحساب الشخصي</span>
              </DropdownMenuItem>
            </Link>
            <DropdownMenuItem
              variant="destructive"
              onSelect={async () => {
                try {
                  await fetch("/users/sign_out", {
                    method: "DELETE",
                    headers: {
                      "X-CSRF-Token":
                        (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)
                          ?.content || "",
                    },
                  });
                  window.location.href = "/";
                } catch (e) {
                  console.error(e);
                }
              }}
            >
              <LogOut />
              <span>تسجيل الخروج</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
